import { useMemo, useState } from "react";
import Chessboard from "../components/chessboard/Chessboard";
import useSound from 'use-sound';
import correctSound from '../assets/sounds/correct.wav';
import wrongSound from '../assets/sounds/wrong.wav';
import pieceMove from '../assets/sounds/pieceMove.mp3';
import { Chess } from "chess.js";
import { ArrowBackOutline } from 'react-ionicons';

function OpeningPuzzle({ puzzle, puzzleCompleted, setPuzzleSelectionMode, onNextPuzzle }) {
    const [squaresToHighlight, setSquaresToHighlight] = useState([]);
    const [fen, setFen] = useState(null);
    const [isAnimating, setIsAnimating] = useState(false);
    const [playedOnce, setPlayedOnce] = useState(false);
    const [solved, setSolved] = useState(false);

    const [playCorrect] = useSound(correctSound, { volume: 0.1 });
    const [playWrong]   = useSound(wrongSound,   { volume: 0.1 });
    const [playMove]    = useSound(pieceMove,     { volume: 0.25 });

    const baseFEN = "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1";

    // Pre-compute whose turn it is in the final puzzle position (before any animation)
    const finalSideToMove = useMemo(() => {
        if (!puzzle?.PGN) return 'w';
        const chess = new Chess(baseFEN);
        puzzle.PGN.split(' ').forEach(m => { try { chess.move(m); } catch (_) {} });
        return chess.turn();
    }, [puzzle?.PGN]);

    const locked = !playedOnce || isAnimating;

    // Board is always shown from the moving side's perspective
    const flipped = finalSideToMove === 'b';

    // Current side to move (for glow highlight) — only meaningful when unlocked
    const sideToMove = fen ? fen.split(' ')[1] : finalSideToMove;

    const checkMove = (moveStr) => {
        if (moveStr === puzzle.Solution) {
            playCorrect();
            setSolved(true);
            puzzleCompleted(puzzle.id);
        } else {
            playWrong();
        }
        setSquaresToHighlight([]);
    };

    const amICorrect = (square) => {
        if (locked || solved) return;
        if (!square) return;
        if (squaresToHighlight.includes(square)) {
            setSquaresToHighlight([]);
        } else if (squaresToHighlight.length > 0) {
            checkMove(`${squaresToHighlight[0]}${square}`);
        } else {
            setSquaresToHighlight([square]);
        }
    };

    const onDropMove = (from, to) => {
        if (locked || solved) return;
        checkMove(`${from}${to}`);
    };

    const beginAnimation = () => {
        setPlayedOnce(true);
        setIsAnimating(true);
        setSolved(false);
        setSquaresToHighlight([]);
        const chess = new Chess(baseFEN);
        setFen(baseFEN);
        let moveIndex = 0;
        const moves = puzzle.PGN.split(' ');

        const interval = setInterval(() => {
            if (moveIndex < moves.length) {
                playMove();
                chess.move(moves[moveIndex]);
                setFen(chess.fen());
                moveIndex++;
            } else {
                clearInterval(interval);
                setIsAnimating(false);
            }
        }, 1500);
    };

    const displayFen = fen ?? baseFEN;

    return (
        <div style={styles.container}>
            <div
                onClick={() => setPuzzleSelectionMode(true)}
                style={{ position: 'absolute', top: 16, left: 10, zIndex: 10, cursor: 'pointer' }}
            >
                <ArrowBackOutline color={'#ffffff'} height="40px" width="40px" />
            </div>

            {/* Board */}
            <div style={{ position: 'relative' }}>
                <Chessboard
                    setClickedSquare={() => {}}
                    amICorrect={amICorrect}
                    boardSize={8}
                    displaysPieces={true}
                    displaysChecker={true}
                    fen={displayFen}
                    squaresToHighlight={squaresToHighlight}
                    flipped={flipped}
                    sideToMove={locked ? null : sideToMove}
                    locked={locked || solved}
                    onDropMove={onDropMove}
                />

                {/* Success overlay */}
                {solved && (
                    <div style={styles.successOverlay}>
                        <div style={styles.successCard}>
                            <div style={{ fontSize: 56 }}>♟</div>
                            <p style={{ fontSize: '1.5rem', fontWeight: 700, color: '#4ade80', margin: '0.5rem 0 0.25rem' }}>
                                Puzzle Complete!
                            </p>
                            <p style={{ color: '#9ca3af', fontSize: '0.95rem', marginBottom: '1.5rem', textAlign: 'center' }}>
                                {puzzle.Opening}
                            </p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', width: '100%' }}>
                                {onNextPuzzle && (
                                    <button
                                        className="btn btn-primary btn-lg w-full"
                                        onClick={onNextPuzzle}
                                    >
                                        Next Puzzle →
                                    </button>
                                )}
                                <button
                                    className="btn btn-ghost w-full"
                                    onClick={() => setPuzzleSelectionMode(true)}
                                >
                                    Back to Puzzles
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Control panel */}
            <div style={styles.controlPanel}>
                <h1 className="mt-10 self-center text-center" style={{ color: 'white', fontSize: '2vw', fontWeight: 600 }}>
                    {puzzle.Opening}
                </h1>
                <h2 className="mt-6" style={{ color: 'white', fontSize: '1.25vw', fontWeight: 600 }}>
                    Rating: {puzzle.Rating}
                </h2>

                <p className="mt-8 mx-4 text-center text-gray-400" style={{ fontSize: '1vw' }}>
                    Watch the animation of the opening moves, then find the best move in the resulting position.
                </p>

                <div className="mt-auto w-full flex items-center flex-col gap-3">
                    {!locked && !solved && (
                        <p style={{ fontSize: '0.85vw' }} className="text-center w-5/6 text-gray-500">
                            {sideToMove === 'b' ? 'Black' : 'White'} to move.
                            {' '}Click a piece then a destination, or drag and drop.
                        </p>
                    )}
                    {isAnimating && (
                        <p style={{ fontSize: '0.85vw' }} className="text-center text-gray-500">
                            Watching opening moves…
                        </p>
                    )}
                    {!isAnimating && (
                        <button
                            onClick={beginAnimation}
                            className="btn btn-lg btn-secondary w-full mt-4"
                        >
                            {playedOnce ? 'Watch Again' : 'Begin Puzzle'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

const styles = {
    container: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-evenly',
        alignItems: 'center',
        fontFamily: 'Raleway, sans-serif',
        height: '100%',
        width: '100%',
        position: 'relative',
    },
    successOverlay: {
        position: 'absolute',
        inset: 0,
        background: 'rgba(0,0,0,0.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 20,
        borderRadius: 20,
    },
    successCard: {
        background: '#1f2937',
        borderRadius: 20,
        padding: '2.5rem 2rem',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '80%',
        maxWidth: 340,
        boxShadow: '0 25px 50px rgba(0,0,0,0.5)',
    },
    controlPanel: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        borderRadius: 5,
        height: '80vh',
        width: '20vw',
        backgroundColor: '#242424',
        padding: 20,
    },
};

export default OpeningPuzzle;
